import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientFilter,
  selectCrossClientPartialResults,
  selectCrossClientResults,
  selectCrossClientResultsExecution,
  setCrossClientResultsMatchFilter,
} from '../../../store/crossClient/crossClientResults'
import { CrossClientQueryPhases, CrossClientQueryResultMatches, type CrossClientQueryResultMatch } from '../../../models/CrossClient'
import { Button } from '../../../components/Button/Button'
import { executionIsRunning } from '../../../store/crossClient/utils'
import accessRequiredIllustration from '../../../assets/images/cross-client-logo.png'

const isOnlyFullMatchFilter = (matches: string[]) => matches.length === 1 && matches[0] === CrossClientQueryResultMatches.MATCH

const ALL_MATCHES: CrossClientQueryResultMatch[] = [
  CrossClientQueryResultMatches.MATCH,
  CrossClientQueryResultMatches.PARTIAL,
  CrossClientQueryResultMatches.NO_MATCH,
]

export const CrossClientTableEmptyState: React.FC = () => {
  const dispatch = useAppDispatch()
  const [, setSearchParams] = useSearchParams()

  const execution = useAppSelector(selectCrossClientResultsExecution)
  const allResults = useAppSelector(selectCrossClientResults)
  const partialResults = useAppSelector(selectCrossClientPartialResults)
  const filter = useAppSelector(selectCrossClientFilter)

  const isRunning = executionIsRunning(execution)
  const isCompleted = execution?.phase === CrossClientQueryPhases.COMPLETED
  const onlyFullMatchFilter = isOnlyFullMatchFilter(filter.matches)

  const partialCount = partialResults.length
  const totalCount = allResults.length
  const clientName = filter.clientName?.trim()

  const { title, description } = clientName
    ? { title: 'No results found', description: `No results found for "${clientName}".` }
    : getEmptyStateContent({ isRunning, onlyFullMatchFilter, partialCount, totalCount })

  const changeFilter = (matches: CrossClientQueryResultMatch[]) => {
    dispatch(setCrossClientResultsMatchFilter(matches))
    setSearchParams(
      (prev) => {
        prev.set('matches', matches.join(','))
        return prev
      },
      { replace: true },
    )
  }

  return (
    <div className='d-flex flex-column align-items-center justify-content-center gap-3 zocks-pt-7.5'>
      <img src={accessRequiredIllustration} alt='' className='cross-client-logo' />
      <div className='d-flex flex-column gap-2 align-items-center justify-content-center cross-client-no-result-content'>
        <h5 className='mb-0'>{title}</h5>
        <p className='paragraph-medium text-center zocks-text-gray-600 mb-0'>{description}</p>
        {isCompleted && !clientName ? (
          onlyFullMatchFilter && partialCount > 0 ? (
            <Button
              variant='header'
              className='button-small px-2 zocks-border-0.5 zocks-border-color-gray-300'
              onClick={() => changeFilter([CrossClientQueryResultMatches.PARTIAL])}
            >
              Show partial matches
            </Button>
          ) : !onlyFullMatchFilter && totalCount > 0 ? (
            <Button variant='header' className='button-small px-2 zocks-border-0.5 zocks-border-color-gray-300' onClick={() => changeFilter(ALL_MATCHES)}>
              Show all matches
            </Button>
          ) : null
        ) : null}
      </div>
    </div>
  )
}

interface EmptyStateContentParams {
  isRunning: boolean
  onlyFullMatchFilter: boolean
  partialCount: number
  totalCount: number
}

const getEmptyStateContent = ({ isRunning, onlyFullMatchFilter, partialCount, totalCount }: EmptyStateContentParams) => {
  if (isRunning && onlyFullMatchFilter) {
    return {
      title: 'No full matches yet',
      description: "The query is still running. Full matches will appear here as they're found. Change the filter above to view all results.",
    }
  }

  if (isRunning) {
    return {
      title: 'No results for this filter',
      description: "The query is still running. Results will appear here as they're found.",
    }
  }

  if (onlyFullMatchFilter) {
    const description =
      partialCount > 0 ? `The query found ${partialCount} partial matches but no clients matched all criteria.` : 'No clients matched all criteria.'
    return { title: 'No full matches found', description }
  }

  const description = totalCount > 0 ? 'The query found matches in other categories.' : 'The query did not find any matches.'
  return { title: 'No results for this filter', description }
}
